<script lang="ts">
  import { onMount } from 'svelte';
  import ChevronLeftIcon from '@lucide/svelte/icons/chevron-left';
  import ChevronRightIcon from '@lucide/svelte/icons/chevron-right';
  import { BrowserScreenshot } from '$lib/components/browser-frame';

  type Phase = {
    label: string;
    /** Headline as explicit lines so each phase breaks where we want. */
    headlineLines: string[];
    lead: string;
    bullets: string[];
  };

  const phases: Phase[] = [
    {
      label: 'Phase 01 · Deploy',
      headlineLines: ['One click. Your cloud.', 'Running.'],
      lead: 'Deploy from Azure Marketplace directly into your tenant. Your infrastructure, your network, your compliance boundary.',
      bullets: [
        '~30 minutes from click to a working instance',
        'Entra ID native — SSO and RBAC from day one',
        'Runs in your Azure subscription, not ours',
      ],
    },
    {
      label: 'Phase 02 · Shape it',
      headlineLines: ['Connect your data.', 'Build your agents.'],
      lead: 'Shape AI to how your business actually operates — not a generic chatbot. Your data sources, your workflows, your language.',
      bullets: [
        'Data sources behind private networks',
        'Visual agent builder + sandbox',
        'IT approval gates and permission scoping',
      ],
    },
    {
      label: 'Phase 03 · It’s yours',
      headlineLines: ['Configure it.', 'Extend it.', 'Build a product on it.'],
      lead: 'Go as deep as you want. Ops teams configure agents. Developers extend with custom code. Product companies ship entire products on the foundation.',
      bullets: [
        'API, MCP, OpenAPI — override anything',
        'White-label or embed — your surface, our foundation',
        'Every interaction governed and auditable',
      ],
    },
  ];

  // Scroll-driven horizontal advance.
  // The outer track is `(phases.length) * 100vh` tall. Inside it sits a
  // sticky 100vh viewport that holds a horizontal track of `phases.length`
  // panels. As the page scrolls through the outer track, we translate the
  // horizontal track left by a fraction of the total scroll distance.
  let trackEl: HTMLDivElement | null = $state(null);
  let railEl: HTMLDivElement | null = $state(null);
  let progress = $state(0); // 0..(phases.length - 1)
  let activePhase = $state(0);
  let prefersReducedMotion = $state(false);
  // Scroll-affordance hints: chevrons on the panel edges pulse in the
  // direction the rail is advancing, so first-time users connect their
  // vertical scroll to the horizontal motion.
  let isScrolling = $state(false);
  let scrollDirection: 'forward' | 'backward' = $state('forward');
  const showBack = $derived(progress > 0.05);
  const showForward = $derived(progress < phases.length - 1.05);
  const primerForward = $derived(activePhase === 0 && !isScrolling);

  onMount(() => {
    // Resolve the scrolling ancestor — the layout uses an inner overflow-y-auto
    // container, so document scroll is a no-op.
    function findScrollContainer(node: HTMLElement | null): HTMLElement | Window {
      let n: HTMLElement | null = node;
      while (n && n !== document.body) {
        const style = getComputedStyle(n);
        if (/(auto|scroll)/.test(style.overflowY) && n.scrollHeight > n.clientHeight) {
          return n;
        }
        n = n.parentElement;
      }
      return window;
    }

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion = mq.matches;
    const onMq = () => (prefersReducedMotion = mq.matches);
    mq.addEventListener('change', onMq);

    if (prefersReducedMotion || !trackEl) {
      return () => mq.removeEventListener('change', onMq);
    }

    const container = findScrollContainer(trackEl);

    let raf = 0;
    let lastProgress = 0;
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    // Map a normalized scroll position [0..1] into a phase progress [0..n-1]
    // with a dwell-transition-dwell rhythm so each phase has visible hold time
    // instead of a constant linear scrub.
    function remap(t: number): number {
      const n = phases.length; // 3
      // Each phase gets a 1/n slice. Within its slice, the first 35% is
      // dwell (centered), the next 65% is the transition out to the next phase.
      const slice = 1 / n;
      const idx = Math.min(n - 1, Math.floor(t / slice));
      const local = (t - idx * slice) / slice; // 0..1 within this slice
      const dwell = 0.35;
      const eased =
        local < dwell
          ? 0
          : Math.min(1, (local - dwell) / (1 - dwell));
      // Smooth the transition with a cubic ease.
      const cubic = eased < 0.5 ? 4 * eased * eased * eased : 1 - Math.pow(-2 * eased + 2, 3) / 2;
      return Math.min(n - 1, idx + cubic);
    }

    const update = () => {
      raf = 0;
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      const containerTop = container instanceof Window ? 0 : (container as HTMLElement).getBoundingClientRect().top;
      // Scroll progress through the track: 0 when the track top hits the
      // container top; 1 when the track has scrolled by (height - 1 viewport).
      const traversed = containerTop - rect.top;
      const denominator = trackEl.offsetHeight - (container instanceof Window ? window.innerHeight : (container as HTMLElement).clientHeight);
      const t = denominator > 0 ? Math.max(0, Math.min(1, traversed / denominator)) : 0;
      const newProgress = remap(t);
      const delta = newProgress - lastProgress;
      if (Math.abs(delta) > 0.0015) {
        isScrolling = true;
        scrollDirection = delta > 0 ? 'forward' : 'backward';
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => { isScrolling = false; }, 450);
      }
      lastProgress = newProgress;
      progress = newProgress;
      activePhase = Math.min(phases.length - 1, Math.round(newProgress));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    if (container instanceof Window) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', update);
    } else {
      (container as HTMLElement).addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', update);
    }

    return () => {
      mq.removeEventListener('change', onMq);
      if (container instanceof Window) {
        window.removeEventListener('scroll', onScroll);
      } else {
        (container as HTMLElement).removeEventListener('scroll', onScroll);
      }
      window.removeEventListener('resize', update);
      if (raf) cancelAnimationFrame(raf);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  });

  const trackTransform = $derived(`translate3d(${-progress * (100 / phases.length)}%, 0, 0)`);

  // Per-phase scroll fraction (0..1) — how far the user has moved through that
  // panel's slice of the rail, *accelerated* so the inner page-scroll reaches
  // its bottom well before the horizontal transition completes (otherwise the
  // bottom of a tall capture is never seen). The image races to fully scrolled
  // by ~60% of the phase, then holds while horizontal advance finishes.
  const INNER_SCROLL_END = 0.55;
  function phaseScroll(i: number): number {
    const local = Math.max(0, Math.min(1, progress - i));
    return Math.min(1, local / INNER_SCROLL_END);
  }
</script>

<!--
  HeroStageSection — black, sticky, horizontal-scroll stage for the 3-phase
  product tour. Sits between the beige hero and §2 The Gap. On reduced-motion
  or mobile, the stage degrades to a vertical stack of three full-width
  black panels with no sticky / no horizontal translate.
-->
<section
  class="hero-stage relative bg-[#0a0a0a] text-white"
  aria-label="Product tour"
>
  <!-- Top edge accent — beige peeking up from below the previous section -->
  <div class="hidden lg:block absolute top-0 inset-x-0 h-px bg-[#222]" aria-hidden="true"></div>

  {#if prefersReducedMotion}
    <!-- Reduced-motion / fallback: vertical stack of black panels -->
    <div class="flex flex-col">
      {#each phases as phase, i}
        <div class="phase-vertical px-8 lg:px-[9.03%] py-[12vh] border-b border-[#1c1c1c]">
          <p class="font-mono text-[12px] uppercase tracking-[0.14em] text-[#ee6018] mb-5">{phase.label}</p>
          <h2 class="font-display font-[300] text-[36px] md:text-[48px] lg:text-[60px] leading-[1.04] tracking-[-0.035em] mb-6 max-w-[820px]">
            {#each phase.headlineLines as line, idx}
              <span class="block {idx === phase.headlineLines.length - 1 ? 'text-white' : 'text-white/85'}">{line}</span>
            {/each}
          </h2>
          <p class="font-body font-light text-[16px] lg:text-[18px] leading-[1.55] text-[#B8B8B8] max-w-[600px] mb-8">
            {phase.lead}
          </p>
          <ul class="flex flex-col gap-3 max-w-[600px]">
            {#each phase.bullets as bullet}
              <li class="flex items-start gap-3 font-body text-[15px] leading-[1.55] text-white/85">
                <span class="mt-[10px] w-[10px] h-px bg-[#ee6018] shrink-0"></span>
                <span>{bullet}</span>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </div>
  {:else}
    <!-- Tall outer track — drives the horizontal advance via scrollTop. -->
    <div bind:this={trackEl} class="relative" style="height: {phases.length * 100}vh;">
      <!-- Sticky viewport pinned at top of scroll container.
           Clip the horizontal rail in an inner wrapper rather than on the
           sticky element itself — overflow on the sticky element is what
           was leaking 50–70px of horizontal scroll into the page. -->
      <div class="sticky top-0 h-screen w-full">
        <!-- Progress indicator — pinned top center. -->
        <div class="absolute top-0 inset-x-0 z-20 px-8 lg:px-[9.03%] pt-7 lg:pt-9 flex items-center justify-end md:justify-between gap-6 pointer-events-none">
          <p class="hidden md:block font-mono text-[10px] uppercase tracking-[0.16em] text-[#878787]">
            The product · in three moves
          </p>
          <div class="flex items-center gap-3">
            {#each phases as p, i}
              <div class="flex items-center gap-2">
                <span
                  class="block h-px transition-[width,background-color] duration-500 ease-out"
                  style="width: {i === activePhase ? '36px' : '14px'}; background-color: {i === activePhase ? '#ee6018' : '#333335'};"
                ></span>
                <span
                  class="font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-500"
                  class:text-white={i === activePhase}
                  class:text-[#5C5855]={i !== activePhase}
                >0{i + 1}</span>
              </div>
            {/each}
          </div>
        </div>

        <!-- Edge chevrons — direction-of-travel hints. The forward chevron
             is primed (full opacity, accent) on phase 01 so first-time users
             see an invitation before they've scrolled. Once scrolling begins,
             both chevrons brighten and slide in the scroll direction. -->
        <div
          class="edge-chevron edge-chevron-back"
          class:visible={showBack}
          class:active={isScrolling && scrollDirection === 'backward'}
          aria-hidden="true"
        >
          <ChevronLeftIcon strokeWidth={1.4} />
        </div>
        <div
          class="edge-chevron edge-chevron-forward"
          class:visible={showForward}
          class:primer={primerForward}
          class:active={isScrolling && scrollDirection === 'forward'}
          aria-hidden="true"
        >
          <ChevronRightIcon strokeWidth={1.4} />
        </div>

        <!-- Clip wrapper: bounded to the sticky viewport, contains the rail. -->
        <div class="absolute inset-0 overflow-hidden">
        <!-- Horizontal rail — translates as you scroll. Sized in % of this
             clip wrapper so it's bounded by the actual rendered width
             (avoids 100vw including scrollbar leakage). -->
        <div
          bind:this={railEl}
          class="rail flex h-full"
          style="width: {phases.length * 100}%; transform: {trackTransform}; will-change: transform;"
        >
          {#each phases as phase, i}
            <div class="phase-panel relative shrink-0 h-full" style="width: {100 / phases.length}%;">
              <div class="phase-pad absolute inset-0 flex items-start lg:items-center pt-16 lg:pt-0">
                <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,460px)_minmax(0,1fr)] gap-8 lg:gap-[72px] items-start lg:items-center w-full max-w-[1480px] mx-auto">
                <!-- Text -->
                <div class="min-w-0 max-w-[540px]">
                  <p class="font-mono text-[12px] uppercase tracking-[0.14em] text-[#ee6018] mb-5">{phase.label}</p>
                  <h2 class="font-display font-[320] text-[28px] md:text-[36px] lg:text-[44px] leading-[1.04] tracking-[-0.03em] mb-7">
                    {#each phase.headlineLines as line, idx}
                      <span class="block {idx === phase.headlineLines.length - 1 ? 'text-white' : 'text-white/70'}">{line}</span>
                    {/each}
                  </h2>
                  <p class="font-body font-light text-[16px] lg:text-[18px] leading-[1.5] tracking-[-0.01em] text-[#B8B8B8] mb-7 max-w-[460px]">
                    {phase.lead}
                  </p>
                  <ul class="flex flex-col gap-[12px] max-w-[460px]">
                    {#each phase.bullets as bullet}
                      <li class="flex items-start gap-3 font-body text-[14.5px] leading-[1.5] text-white/85">
                        <span class="mt-[10px] w-[12px] h-px bg-[#ee6018] shrink-0"></span>
                        <span>{bullet}</span>
                      </li>
                    {/each}
                  </ul>
                </div>

                <!-- Visual -->
                <div class="min-w-0 flex items-center lg:justify-end justify-center">
                  {#if i === 0}
                    <!-- Phase 1: Deploy — real Corral onboarding screenshot in Chrome (Windows). -->
                    <BrowserScreenshot
                      class="phase-visual"
                      frame="chrome"
                      theme="light"
                      size="sm"
                      url="app.corral.ai/onboarding"
                      tabTitle="Onboarding · Corral"
                      src="/shared/images/screenshots/onboarding-light.png"
                      alt="Corral deployment progress — Microsoft Azure tenant setup with phase-by-phase checklist"
                      contentAspect="16 / 11"
                      scroll={phaseScroll(0)}
                      imgLoading="eager"
                    />
                  {:else if i === 1}
                    <!-- Phase 2: Shape it -->
                    <div class="phase-visual bg-white text-[#020202] border border-[#D3D3D3] shadow-[0_30px_120px_-30px_rgba(238,96,24,0.18),0_30px_60px_-20px_rgba(0,0,0,0.6)]">
                      <div class="flex items-center justify-between px-4 py-2 border-b border-[#D3D3D3] bg-[#ededea]">
                        <div class="flex items-center gap-2">
                          <img src="/shared/images/logos/mark-corral-black.svg" alt="Corral" class="w-[14px] h-[14px]" />
                          <span class="font-mono text-[11px] text-[#282828]">Corral · Agent Builder</span>
                        </div>
                        <span class="font-mono text-[11px] text-[#5C5855]">acme.corp · production</span>
                      </div>
                      <div class="grid grid-cols-[110px_1fr]">
                        <div class="border-r border-[#eee] bg-[#fafafa] flex flex-col py-4 text-[11px] font-mono">
                          <span class="px-4 py-[6px] text-[#5C5855]">Catalog</span>
                          <span class="px-4 py-[6px] bg-[#ee6018]/10 text-[#ee6018] border-l-2 border-[#ee6018]">Agents</span>
                          <span class="px-4 py-[6px] text-[#5C5855]">Data sources</span>
                          <span class="px-4 py-[6px] text-[#5C5855]">Tools</span>
                          <span class="px-4 py-[6px] text-[#5C5855]">Policies</span>
                        </div>
                        <div class="p-7 lg:p-8 flex flex-col gap-6 relative">
                          <div>
                            <p class="font-display text-[17px] text-[#020202]">Support Agent</p>
                            <p class="font-mono text-[11px] text-[#5C5855] mt-1">scope: read:tickets · write:drafts</p>
                          </div>
                          <div class="relative flex flex-col items-center gap-0 min-h-[180px]">
                            <div class="flex gap-3">
                              <div class="bg-[#f9f9f4] border border-[#D3D3D3] px-3 py-2 font-mono text-[11px] text-[#282828]">Zendesk</div>
                              <div class="bg-[#f9f9f4] border border-[#D3D3D3] px-3 py-2 font-mono text-[11px] text-[#282828]">Product Docs</div>
                              <div class="bg-[#f9f9f4] border border-[#D3D3D3] px-3 py-2 font-mono text-[11px] text-[#282828]">Postgres</div>
                            </div>
                            <div class="w-px h-8 bg-[#D3D3D3]"></div>
                            <div class="bg-black text-white px-5 py-3 font-mono text-[12px] flex items-center gap-2">
                              <span class="w-[6px] h-[6px] rounded-full bg-[#ee6018]"></span>
                              Agent · gpt-4 · CRF-on
                            </div>
                            <div class="w-px h-8 bg-[#D3D3D3]"></div>
                            <div class="flex gap-3">
                              <div class="bg-white border border-[#D3D3D3] px-3 py-2 font-mono text-[11px] text-[#282828]">create_ticket()</div>
                              <div class="bg-white border border-[#D3D3D3] px-3 py-2 font-mono text-[11px] text-[#282828]">issue_refund(&lt;100)</div>
                            </div>
                          </div>
                          <div class="flex items-center gap-2 font-mono text-[11px] text-[#5C5855] border-t border-[#eee] pt-3">
                            <span class="text-[#0b8a3e]">●</span> Sandbox passing · 12/12 eval suites
                          </div>
                        </div>
                      </div>
                    </div>
                  {:else}
                    <!-- Phase 3: It's yours -->
                    <div class="phase-visual bg-white text-[#020202] border border-[#D3D3D3] shadow-[0_30px_120px_-30px_rgba(238,96,24,0.18),0_30px_60px_-20px_rgba(0,0,0,0.6)]">
                      <div class="flex items-center gap-2 px-3 py-2 border-b border-[#D3D3D3] bg-[#ededea]">
                        <div class="flex gap-[6px]">
                          <div class="w-[10px] h-[10px] rounded-full bg-[#ff5f57]"></div>
                          <div class="w-[10px] h-[10px] rounded-full bg-[#febc2e]"></div>
                          <div class="w-[10px] h-[10px] rounded-full bg-[#28c940]"></div>
                        </div>
                        <div class="flex-1 flex justify-center">
                          <div class="font-mono text-[11px] text-[#5C5855] bg-white border border-[#D3D3D3] rounded-sm px-3 py-[3px]">
                            app.yourcompany.com
                          </div>
                        </div>
                      </div>
                      <div class="relative min-h-[380px] bg-gradient-to-b from-white to-[#f9f9f4]">
                        <div class="flex items-center justify-between px-7 py-5 border-b border-[#eee]">
                          <div class="flex items-center gap-2">
                            <div class="w-[18px] h-[18px] bg-[#020202] rounded-sm"></div>
                            <span class="font-display text-[15px] text-[#020202] tracking-tight">Your Company</span>
                          </div>
                          <div class="flex gap-5 font-body text-[12px] text-[#5C5855]">
                            <span>Product</span><span>Customers</span><span>Pricing</span>
                          </div>
                        </div>
                        <div class="px-7 py-10 flex flex-col gap-3 max-w-[360px]">
                          <p class="font-mono text-[10px] uppercase tracking-wider text-[#5C5855]">your positioning</p>
                          <p class="font-display text-[22px] text-[#020202] leading-tight">Your product. Your surface. Your AI.</p>
                          <p class="font-body text-[13px] text-[#5C5855] leading-relaxed">
                            Corral runs underneath. Your customers never need to know.
                          </p>
                        </div>
                        <div class="absolute bottom-5 right-5 w-[250px] bg-white border border-[#D3D3D3] shadow-[0_16px_40px_-16px_rgba(0,0,0,0.22)]">
                          <div class="px-3 py-2 border-b border-[#eee] flex items-center gap-2 bg-[#fafafa]">
                            <span class="w-[8px] h-[8px] rounded-full bg-[#0b8a3e]"></span>
                            <span class="font-display text-[12px] text-[#020202]">Ask your product</span>
                          </div>
                          <div class="px-3 py-3 flex flex-col gap-2 font-body text-[12px]">
                            <div class="bg-[#f3f3f0] px-3 py-2 text-[#282828] self-start max-w-[85%]">
                              How do I export last quarter's invoices?
                            </div>
                            <div class="bg-[#ee6018] text-white px-3 py-2 self-end max-w-[85%]">
                              I pulled 14 invoices from Q4. Export as CSV?
                            </div>
                          </div>
                          <div class="px-3 py-[6px] border-t border-[#eee] font-mono text-[9px] uppercase tracking-wider text-[#5C5855] flex items-center justify-between">
                            <span>CRF · auditable</span>
                            <span>→</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  {/if}
                </div>
                </div>
              </div>

              <!-- Subtle index marker, bottom-right corner of each panel -->
              <div class="absolute bottom-7 lg:bottom-9 right-8 lg:right-[9.03%] flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C5855]">
                <span>0{i + 1} / 0{phases.length}</span>
                {#if i < phases.length - 1}
                  <span class="text-[#ee6018]">↓ scroll</span>
                {:else}
                  <span class="text-[#ee6018]">↓ continue</span>
                {/if}
              </div>
            </div>
          {/each}
        </div>
        </div>
      </div>
    </div>
  {/if}
</section>

<style>
  .phase-visual {
    width: 100%;
    /* Below lg the image stacks under the text — keep it modest so the panel fits in 100vh. */
    max-width: 540px;
  }
  @media (min-width: 1024px) {
    /* Fluid scale lg+ — no stepped jumps at xl/2xl. ~62% of viewport width,
       floored at 760px (so it's substantial at 1024) and capped at 1080px. */
    .phase-visual {
      max-width: clamp(760px, 62vw, 1080px);
    }
  }

  /* Viewport-relative padding so each panel's content aligns with the
     same 9.03% grid lines used by the rest of the site. Using vw here
     because the panel itself is sized as % of the rail (not vw), so
     percentage padding would resolve against the panel's own width. */
  .phase-pad {
    padding-inline: 2rem;
  }
  @media (min-width: 1024px) {
    .phase-pad {
      padding-inline: 9.03vw;
    }
  }

  /* Edge chevrons — hidden below lg (the side margins get tight on tablet),
     low-opacity at rest, brighten and pulse in the scroll direction. */
  .edge-chevron {
    position: absolute;
    top: 50%;
    z-index: 20;
    pointer-events: none;
    width: 28px;
    height: 28px;
    color: #5C5855;
    opacity: 0;
    transform: translateY(-50%);
    transition: opacity 500ms ease-out, color 300ms ease-out;
    display: none;
  }
  .edge-chevron :global(svg) {
    width: 100%;
    height: 100%;
    display: block;
  }
  @media (min-width: 1024px) {
    .edge-chevron {
      display: block;
    }
  }
  .edge-chevron-back {
    left: max(18px, 2.2vw);
  }
  .edge-chevron-forward {
    right: max(18px, 2.2vw);
  }
  .edge-chevron.visible {
    opacity: 0.32;
  }
  /* Primer state — forward chevron only, while the user is on phase 01 and
     hasn't started scrolling. Prominent so it reads as an invitation; the
     pulse animation is reserved for the active-scroll state. */
  .edge-chevron-forward.primer {
    opacity: 1;
    color: #ee6018;
  }
  .edge-chevron-back.active {
    opacity: 1;
    color: #ee6018;
    animation: nudge-back 900ms ease-in-out infinite;
  }
  .edge-chevron-forward.active {
    opacity: 1;
    color: #ee6018;
    animation: nudge-forward 900ms ease-in-out infinite;
  }

  @keyframes nudge-forward {
    0%, 100% { transform: translate(0, -50%); }
    50% { transform: translate(8px, -50%); }
  }
  @keyframes nudge-back {
    0%, 100% { transform: translate(0, -50%); }
    50% { transform: translate(-8px, -50%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .edge-chevron-back.active,
    .edge-chevron-forward.active {
      animation: none;
    }
  }

  /* Subtle ambient: a tinted radial behind the panels suggesting depth.
     Inset constrained to 0 horizontally so the gradient never bleeds past
     the section width — that bleed was creating phantom horizontal scroll. */
  .hero-stage::before {
    content: "";
    position: absolute;
    inset: -10% 0 0 0;
    background: radial-gradient(60% 50% at 50% 30%, rgba(238, 96, 24, 0.12), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
</style>
